import {
  CACHE_KEYS,
  CACHE_KEY_PREFIX,
  CACHE_TTL_HOURS,
  NEVER_EXPIRES,
  DEFAULT_SETTINGS,
  EMPTY_PROGRESS,
  accumulateUsageHours,
  epgCacheKey,
  parseSettings,
  readCacheVerdict,
  withEpisodeCounts,
  movieDetailCacheKey,
  seriesDetailCacheKey,
  writeCacheEnvelope,
  type Channel,
  type PlayerSettings,
  type Credentials,
  type Program,
  type MovieDetail,
  type SeriesDetail,
  type VodItem,
  type VodKind,
  type WatchProgress,
} from '@player/domain';
import type { CatalogResult, ChannelCategory, GatewayPort, StoragePort } from '@player/ports';

/**
 * Il catalogo che vive sull'apparecchio.
 *
 * ## Cosa toglie
 *
 * All'avvio l'app chiedeva **tutto**, ogni volta: il listino (4,91 MB su un catalogo vero), le
 * categorie, e l'EPG di ogni canale sfiorato. Nessuno di quei dati cambia mentre si guarda la
 * televisione, e inizializzare il catalogo era di per sé un carico — sullo stesso gateway che conta
 * le richieste per indirizzo e blocca chi esagera.
 *
 * ## Come decide
 *
 * Le decisioni non sono qui: stanno in `@player/domain/cache`, dove si provano senza un browser.
 * Qui c'è solo **leggere, scrivere e chiedere** — e la regola che tiene insieme le tre cose:
 *
 * - all'accesso si ricontrolla sempre, ma per il listino il ricontrollo è un `ETag` e il server
 *   risponde `304` senza mandare niente;
 * - dopo, si ricontrolla ogni tot **ore d'uso**, non di calendario;
 * - quando il ricontrollo fallisce si **tiene quel che c'è**, e lo si dice a chi ha chiamato.
 *
 * ## L'`ETag` che moriva
 *
 * Era già nel contratto e già usato, ma viveva in memoria: alla chiusura dell'app spariva, quindi la
 * riapertura successiva riscaricava megabyte per scoprire che erano identici. Archiviarlo accanto ai
 * dati è tutta la differenza fra «ricontrollo» e «riscarico».
 */

/** Quanto spesso si somma il tempo d'uso. Un minuto: abbastanza fine, abbastanza raro. */
export const USAGE_TICK_MS = 60_000;

const USAGE_KEY = `${CACHE_KEY_PREFIX}usageHours`;

/** Quanti titoli recenti si ricordano. Oltre, «Continua a guardare» non li mostra comunque. */
export const RECENT_VOD_LIMIT = 40;

/**
 * Quanti canali per richiesta quando si rinfresca la guida.
 *
 * Duecento è il tetto che il backend dichiara su `get_guide_window`: chiederne di più è un rifiuto,
 * chiederne molti meno moltiplica le richieste per niente.
 */
const GUIDE_BATCH = 200;

/**
 * Il respiro fra un blocco e l'altro.
 *
 * Su dodicimila canali sono sessanta richieste: sparate insieme sono indistinguibili da un attacco
 * per chi le riceve, e il gateway le conterebbe come tali. Mezzo secondo distribuisce il giro su
 * mezzo minuto — che per un lavoro di fondo che nessuna schermata aspetta è gratis.
 */
const GUIDE_BATCH_PAUSE_MS = 500;

/**
 * Ogni quanto si rinfresca la guida, in millisecondi.
 *
 * Un'ora: è la granularità con cui un palinsesto cambia davvero, ed è quel che è stato chiesto.
 */
export const GUIDE_REFRESH_MS = 3_600_000;

const pause = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Quanto guarda avanti una lettura della guida dall'istante chiesto.
 *
 * Generoso di proposito: chi legge passa l'inizio della **mezz'ora** che si sta guardando, mentre il
 * giro di fondo scarica una finestra ancorata all'**ora** e larga qualche ora. Una lettura più stretta
 * dell'archivio nasconderebbe programmi che ci sono, e la griglia mostrerebbe dei buchi dove c'è il
 * dato — che è il difetto da cui veniamo, solo più piccolo.
 *
 * Non è un tetto sui dati: è quanto ne serve a chi disegna una schermata.
 */
const WINDOW_READ_MS = 12 * 60 * 60_000;

export class CatalogCache {
  private usageHours = 0;

  constructor(
    private readonly storage: StoragePort,
    private readonly gateway: GatewayPort,
  ) {}

  /** Legge il contatore d'uso archiviato. Va chiamata prima di tutto il resto. */
  async load(): Promise<void> {
    const raw = await this.storage.readLocal(USAGE_KEY);
    const parsed = raw === null ? 0 : Number(raw);
    this.usageHours = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  /**
   * Somma un pezzo di tempo d'uso e lo archivia.
   *
   * Chi sta sopra la chiama a intervalli mentre l'app è in uso. Un passo troppo lungo — uno standby —
   * lo scarta il dominio, non questa classe.
   */
  async tick(elapsedMs: number): Promise<void> {
    const next = accumulateUsageHours(this.usageHours, elapsedMs);
    if (next === this.usageHours) return;
    this.usageHours = next;
    await this.storage.writeLocal(USAGE_KEY, String(next));
  }

  /**
   * Il listino dei canali.
   *
   * `revalidate` è vero all'accesso: si ricontrolla comunque, ma con l'`ETag` archiviato — quindi
   * quasi sempre è un `304` e non un download. Fuori dall'accesso si ricontrolla solo a scadenza.
   */
  async channels(
    credentials: Credentials,
    revalidate: boolean,
  ): Promise<{ channels: ReadonlyArray<Channel>; fromCache: boolean }> {
    const raw = await this.storage.readLocal(CACHE_KEYS.channels);
    const verdict = readCacheVerdict<ReadonlyArray<Channel>>(raw, this.usageHours, CACHE_TTL_HOURS.channels);

    if (verdict.kind === 'stale-version') {
      // Un formato vecchio non si interpreta col formato nuovo: si butta. Il guasto che ne uscirebbe
      // non somiglia a un problema di archivio, somiglia a un catalogo corrotto.
      await this.storage.removeLocal(CACHE_KEYS.channels);
    }

    const cached = verdict.kind === 'fresh' || verdict.kind === 'expired' ? verdict : null;
    if (cached !== null && verdict.kind === 'fresh' && !revalidate) {
      return { channels: cached.data, fromCache: true };
    }

    try {
      const result: CatalogResult = await this.gateway.listChannels(credentials, cached?.etag ?? null);
      if (result.notModified && cached !== null) {
        // Identico: si riscrive solo l'istante, così il prossimo controllo riparte da adesso e non si
        // ricontrolla a ogni apertura.
        await this.storage.writeLocal(
          CACHE_KEYS.channels,
          writeCacheEnvelope(cached.data, this.usageHours, cached.etag),
        );
        return { channels: cached.data, fromCache: true };
      }
      await this.storage.writeLocal(
        CACHE_KEYS.channels,
        writeCacheEnvelope(result.channels, this.usageHours, result.etag),
      );
      return { channels: result.channels, fromCache: false };
    } catch (error) {
      /*
        Il ricontrollo fallito **non butta via quel che si ha**.

        Un catalogo di ieri è enormemente meglio di nessun catalogo: senza, l'app si apre vuota per
        un problema di rete passeggero. Se però non c'è nemmeno quello, l'errore risale — non si
        mostra una lista vuota fingendo che il catalogo sia finito.
      */
      if (cached !== null) return { channels: cached.data, fromCache: true };
      throw error;
    }
  }

  /** I nomi delle categorie, con la stessa regola del listino ma senza `ETag`. */
  async categories(credentials: Credentials, revalidate: boolean): Promise<ReadonlyMap<string, string>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.categories);
    const verdict = readCacheVerdict<ReadonlyArray<ChannelCategory>>(
      raw,
      this.usageHours,
      CACHE_TTL_HOURS.categories,
    );
    if (verdict.kind === 'stale-version') await this.storage.removeLocal(CACHE_KEYS.categories);

    const cached = verdict.kind === 'fresh' || verdict.kind === 'expired' ? verdict.data : null;
    if (cached !== null && verdict.kind === 'fresh' && !revalidate) return toMap(cached);

    try {
      const list = await this.gateway.listCategories(credentials);
      await this.storage.writeLocal(CACHE_KEYS.categories, writeCacheEnvelope(list, this.usageHours));
      return toMap(list);
    } catch {
      // I nomi che non arrivano non impediscono di guardare la TV: la colonna resta usabile con le
      // voci «senza nome» e i conteggi.
      return toMap(cached ?? []);
    }
  }

  /**
   * I programmi di una finestra, per i canali che **non si hanno già**.
   *
   * Restituisce quel che c'era in archivio più quel che è arrivato. La chiave è per canale e per
   * istante d'inizio della finestra: una finestra diversa è un dato diverso, e confonderle
   * mostrerebbe i programmi di stamattina come se fossero di adesso.
   */
  /**
   * I programmi della guida, **dall'archivio locale e basta**.
   *
   * ## Perché non chiede niente
   *
   * Chiedeva: a ogni sosta dello scorrimento la guida domandava i programmi dei canali che non
   * aveva, e su un catalogo vero quelle richieste non finivano mai — un canale nuovo a ogni riga,
   * una finestra nuova a ogni ora. Il gateway le vedeva tutte, e chi sfogliava si faceva bloccare
   * l'indirizzo.
   *
   * Adesso la guida **legge e basta**: quel che c'è si disegna, quel che manca resta vuoto finché
   * `refreshGuide` non passa. Chi scorre non genera traffico, mai.
   *
   * La scadenza non si guarda più qui. Un programma vecchio di due ore è comunque meglio di una
   * riga vuota — dice che alle 21 c'era il telegiornale, e alle 21 c'era davvero — e chi decide
   * quando rinfrescare è il giro di fondo, non chi scorre.
   */
  async programs(
    channelIds: ReadonlyArray<string>,
    windowStartMs: number,
  ): Promise<ReadonlyMap<string, ReadonlyArray<Program>>> {
    const out = new Map<string, ReadonlyArray<Program>>();
    for (const channelId of channelIds) {
      const raw = await this.storage.readLocal(epgCacheKey(channelId));
      const verdict = readCacheVerdict<ReadonlyArray<Program>>(raw, this.usageHours, NEVER_EXPIRES);
      if (verdict.kind !== 'fresh' && verdict.kind !== 'expired') continue;
      /*
        Il filtro per tempo sta **qui**, non nella chiave.

        Quando l'intervallo faceva parte della chiave, chi scriveva e chi leggeva ne usavano due
        diversi — l'ora tonda il giro di fondo, la mezz'ora la griglia — e non si incontravano mai.
        Adesso in archivio c'è la guida del canale e la finestra è una domanda che si fa ai dati.
      */
      const inWindow = verdict.data.filter(
        (program) => program.endMs > windowStartMs && program.startMs < windowStartMs + WINDOW_READ_MS,
      );
      /*
        Si risponde **anche a vuoto**, purché il canale sia in archivio.

        «Non ho programmi per questo canale» e «di questo canale non so niente» sono due cose diverse:
        su stage ci sono canali che una guida non ce l'hanno affatto, e confonderli con quelli mai
        scaricati li rimetterebbe in coda a ogni giro, per sempre.
      */
      out.set(channelId, inWindow);
    }
    return out;
  }

  /**
   * Scarica la guida di una finestra per **tutti** i canali, e la mette in archivio.
   *
   * È il giro di fondo: parte all'avvio e poi ogni ora. Nessuna schermata lo aspetta — la guida
   * disegna quel che c'è — quindi può prendersi il tempo che serve.
   *
   * ## A blocchi, e con un respiro in mezzo
   *
   * Duecento canali per richiesta, che è il tetto del backend: su dodicimila canali sono sessanta
   * richieste. Una pausa fra un blocco e l'altro, perché sessanta richieste sparate insieme sono
   * indistinguibili da un attacco per chi le riceve — e il gateway le conterebbe come tali.
   *
   * L'ordine è quello dei canali, cioè quello della guida: le prime righe si riempiono per prime,
   * e chi apre la guida subito dopo l'avvio vede qualcosa mentre il resto arriva.
   *
   * Un blocco che fallisce **non ferma gli altri**: la guida di metà catalogo vale più di niente, e
   * l'ora dopo si riprova comunque.
   */
  async refreshGuide(
    credentials: Credentials,
    channelIds: ReadonlyArray<string>,
    windowStartMs: number,
    windowEndMs: number,
    onProgress?: (done: number, total: number) => void,
  ): Promise<void> {
    for (let from = 0; from < channelIds.length; from += GUIDE_BATCH) {
      const batch = channelIds.slice(from, from + GUIDE_BATCH);

      try {
        const arrived = await this.gateway.loadGuideWindow(credentials, {
          channelIds: batch,
          fromMs: windowStartMs,
          toMs: windowEndMs,
        });

        const byChannel = new Map<string, Program[]>();
        // Anche i canali **senza** programmi si archiviano: altrimenti un canale che la guida non
        // copre resterebbe «da scaricare» a ogni giro, per sempre.
        for (const channelId of batch) byChannel.set(channelId, []);
        for (const program of arrived) byChannel.get(program.channelId)?.push(program);

        for (const [channelId, programs] of byChannel) {
          await this.storage.writeLocal(
            epgCacheKey(channelId),
            writeCacheEnvelope(programs, this.usageHours),
          );
        }
      } catch {
        // Si prosegue: metà guida vale più di niente, e fra un'ora si riprova comunque. Fermarsi
        // qui lascerebbe il resto del catalogo senza programmi fino al prossimo avvio.
      }

      onProgress?.(Math.min(from + GUIDE_BATCH, channelIds.length), channelIds.length);
      if (from + GUIDE_BATCH < channelIds.length) await pause(GUIDE_BATCH_PAUSE_MS);
    }
  }

  /**
   * Il catalogo on demand, per tipo.
   *
   * Nessun `ETag` da spendere — il livello compatibile non ne manda su queste azioni — quindi ogni
   * ricontrollo è un download intero: dodici ore d'uso, e un errore **non butta via** quel che c'è.
   * Se non c'è nemmeno quello l'errore risale: una vetrina vuota che sembra un catalogo senza film è
   * peggio di un messaggio che dice cosa non è arrivato.
   */
  async vod(
    credentials: Credentials,
    kind: VodKind,
    revalidate: boolean,
  ): Promise<{ items: ReadonlyArray<VodItem>; fromCache: boolean }> {
    const key = kind === 'movie' ? CACHE_KEYS.movies : CACHE_KEYS.series;
    const raw = await this.storage.readLocal(key);
    const verdict = readCacheVerdict<ReadonlyArray<VodItem>>(raw, this.usageHours, CACHE_TTL_HOURS.vod);
    if (verdict.kind === 'stale-version') await this.storage.removeLocal(key);

    const cached = verdict.kind === 'fresh' || verdict.kind === 'expired' ? verdict.data : null;
    if (cached !== null && verdict.kind === 'fresh' && !revalidate) {
      return { items: await this.withSeasonCounts(kind, cached), fromCache: true };
    }

    try {
      const items = await this.gateway.listVod(credentials, kind);
      await this.storage.writeLocal(key, writeCacheEnvelope(items, this.usageHours));
      return { items: await this.withSeasonCounts(kind, items), fromCache: false };
    } catch (error) {
      if (cached !== null) return { items: await this.withSeasonCounts(kind, cached), fromCache: true };
      throw error;
    }
  }

  /**
   * Innesta nel listino delle serie il conteggio degli episodi già noto.
   *
   * **Si innesta qui e non in chi chiama** perché i chiamanti sono quattro — home, vetrina on
   * demand, ricerca, aggregatore — e a tutti serve per la stessa ragione: `watchStateOf` conta gli
   * episodi da `episodesPerSeason`, e senza il conteggio una serie a metà risulta mai iniziata.
   * Farlo in un posto solo è ciò che impedisce che il quinto chiamante se ne dimentichi.
   *
   * L'innesto **non tocca quel che è archiviato**: il listino resta com'è arrivato dal gateway, e
   * l'indice delle stagioni resta un archivio a sé. Mescolarli vorrebbe dire riscrivere megabyte di
   * listino ogni volta che si apre la scheda di una serie.
   */
  private async withSeasonCounts(
    kind: VodKind,
    items: ReadonlyArray<VodItem>,
  ): Promise<ReadonlyArray<VodItem>> {
    if (kind !== 'series') return items;
    const counts = await this.seriesSeasons();
    if (counts.size === 0) return items;
    return items.map((item) => withEpisodeCounts(item, counts.get(item.id) ?? []));
  }

  /** L'indice delle stagioni: quante puntate ha ogni stagione, per serie già aperta. */
  async seriesSeasons(): Promise<ReadonlyMap<string, ReadonlyArray<number>>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.seriesSeasons);
    if (raw === null || raw === '') return new Map();
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null || typeof parsed !== 'object') return new Map();
      const out = new Map<string, ReadonlyArray<number>>();
      for (const [seriesId, value] of Object.entries(parsed as Record<string, unknown>)) {
        // Una voce malformata si salta, non svuota l'indice: un archivio scritto a mano o da una
        // versione precedente non deve far sparire l'avanzamento di tutte le altre serie.
        if (!Array.isArray(value)) continue;
        const counts = value.filter((entry): entry is number => Number.isInteger(entry) && entry >= 0);
        if (counts.length > 0) out.set(seriesId, counts);
      }
      return out;
    } catch {
      return new Map();
    }
  }

  /** I nomi delle categorie on demand, con la stessa regola dei canali. */
  async vodCategories(
    credentials: Credentials,
    kind: VodKind,
    revalidate: boolean,
  ): Promise<ReadonlyMap<string, string>> {
    return toMap(await this.vodCategoryList(credentials, kind, revalidate));
  }

  /**
   * Le categorie on demand **intere**, non ridotte a una mappa di nomi.
   *
   * Serve da quando le categorie hanno un padre: l'albero si costruisce da `parentId`, e una mappa
   * `id → nome` lo butta via. `vodCategories` resta, perché a chi disegna una fascia serve solo il
   * nome, e passa da qui — **una sola lettura dell'archivio**, non due implementazioni che
   * divergeranno al primo campo aggiunto.
   */
  async vodCategoryList(
    credentials: Credentials,
    kind: VodKind,
    revalidate: boolean,
  ): Promise<ReadonlyArray<ChannelCategory>> {
    const key = kind === 'movie' ? CACHE_KEYS.movieCategories : CACHE_KEYS.seriesCategories;
    const raw = await this.storage.readLocal(key);
    const verdict = readCacheVerdict<ReadonlyArray<ChannelCategory>>(
      raw,
      this.usageHours,
      CACHE_TTL_HOURS.vod,
    );
    if (verdict.kind === 'stale-version') await this.storage.removeLocal(key);

    const cached = verdict.kind === 'fresh' || verdict.kind === 'expired' ? verdict.data : null;
    if (cached !== null && verdict.kind === 'fresh' && !revalidate) return cached;

    try {
      const list = await this.gateway.listVodCategories(credentials, kind);
      await this.storage.writeLocal(key, writeCacheEnvelope(list, this.usageHours));
      return list;
    } catch {
      // Senza i nomi la vetrina perde le fasce di genere, non la vetrina: gli ultimi arrivi si
      // vedono comunque. Vedi `vodGenres`, che salta le categorie senza nome invece di scrivere UUID.
      return cached ?? [];
    }
  }

  /**
   * La scheda di una serie, dall'archivio quando c'è.
   *
   * Ventiquattro ore d'uso: le stagioni non cambiano mentre si guarda una serie, e questa è la
   * chiamata che si paga **aprendo una scheda** — cioè nel momento in cui qualcuno sta aspettando.
   */
  async seriesDetail(credentials: Credentials, seriesId: string): Promise<SeriesDetail> {
    const key = seriesDetailCacheKey(seriesId);
    const raw = await this.storage.readLocal(key);
    const verdict = readCacheVerdict<SeriesDetail>(raw, this.usageHours, CACHE_TTL_HOURS.seriesDetail);
    if (verdict.kind === 'stale-version') await this.storage.removeLocal(key);
    if (verdict.kind === 'fresh') {
      // Anche dall'archivio: l'indice è nato dopo le schede, e un apparecchio che ha già la scheda in
      // casa non deve aspettare la scadenza per tornare a comparire in «Continua a guardare».
      await this.rememberSeasons(seriesId, verdict.data);
      return verdict.data;
    }

    try {
      const detail = await this.gateway.readSeries(credentials, seriesId);
      await this.storage.writeLocal(key, writeCacheEnvelope(detail, this.usageHours));
      await this.rememberSeasons(seriesId, detail);
      return detail;
    } catch (error) {
      if (verdict.kind === 'expired') {
        await this.rememberSeasons(seriesId, verdict.data);
        return verdict.data;
      }
      throw error;
    }
  }

  /**
   * Annota quante puntate ha ogni stagione di questa serie.
   *
   * Si riscrive solo quando il conteggio **cambia davvero**: aprire la scheda di una serie è un
   * gesto frequente, e una scrittura nell'archivio locale a ogni apertura su un televisore del 2017
   * è un costo che non compra niente.
   */
  private async rememberSeasons(seriesId: string, detail: SeriesDetail): Promise<void> {
    const counts = detail.seasons.map((season) => season.episodes.length);
    if (counts.length === 0) return;

    const index = await this.seriesSeasons();
    const before = index.get(seriesId);
    if (before !== undefined && before.length === counts.length) {
      if (before.every((value, position) => value === counts[position])) return;
    }

    const next: Record<string, ReadonlyArray<number>> = {};
    for (const [id, value] of index) next[id] = value;
    next[seriesId] = counts;
    await this.storage.writeLocal(CACHE_KEYS.seriesSeasons, JSON.stringify(next));
  }

  /**
   * La scheda di un film, dall'archivio quando c'è.
   *
   * Stessa regola delle serie: ventiquattro ore d'uso, e si chiede **aprendo la scheda** — cioè nel
   * momento in cui qualcuno sta aspettando. Trama, voto e durata non cambiano mentre si guarda.
   */
  async movieDetail(credentials: Credentials, movieId: string): Promise<MovieDetail> {
    const key = movieDetailCacheKey(movieId);
    const raw = await this.storage.readLocal(key);
    const verdict = readCacheVerdict<MovieDetail>(raw, this.usageHours, CACHE_TTL_HOURS.seriesDetail);
    if (verdict.kind === 'stale-version') await this.storage.removeLocal(key);
    if (verdict.kind === 'fresh') return verdict.data;

    try {
      const detail = await this.gateway.readMovie(credentials, movieId);
      await this.storage.writeLocal(key, writeCacheEnvelope(detail, this.usageHours));
      return detail;
    } catch (error) {
      if (verdict.kind === 'expired') return verdict.data;
      throw error;
    }
  }

  /**
   * Quanto si è visto di ogni titolo, su questo apparecchio.
   *
   * Non passa da `readCacheVerdict`: **non scade**. È un fatto di chi guarda e non una copia di
   * qualcosa del server, e una scadenza qui vorrebbe dire dimenticare dove si era arrivati perché
   * sono passate sei ore di televisione.
   */
  async progress(): Promise<WatchProgress> {
    const raw = await this.storage.readLocal(CACHE_KEYS.progress);
    if (raw === null || raw === '') return EMPTY_PROGRESS;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null || typeof parsed !== 'object') return EMPTY_PROGRESS;
      const record = parsed as Partial<WatchProgress>;
      return {
        movies: typeof record.movies === 'object' && record.movies !== null ? record.movies : {},
        episodes:
          typeof record.episodes === 'object' && record.episodes !== null ? record.episodes : {},
      };
    } catch {
      return EMPTY_PROGRESS;
    }
  }

  async saveProgress(progress: WatchProgress): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.progress, JSON.stringify(progress));
  }

  /**
   * Gli ultimi titoli toccati, dal più recente.
   *
   * Decidono l'ordine di «Continua a guardare»: senza, quella fascia esce nell'ordine del catalogo,
   * e il film di ieri sera finisce in fondo dietro venti titoli lasciati a metà mesi fa.
   */
  async recentVod(): Promise<ReadonlyArray<string>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.recentVod);
    if (raw === null || raw === '') return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  /** «La mia lista»: gli identificativi messi da parte su questo apparecchio. */
  async myList(): Promise<ReadonlyArray<string>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.myList);
    if (raw === null || raw === '') return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  async saveMyList(ids: ReadonlyArray<string>): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.myList, JSON.stringify(ids));
  }

  /** Mette un titolo in testa ai recenti, senza duplicarlo. */
  async rememberVod(contentId: string): Promise<ReadonlyArray<string>> {
    const before = await this.recentVod();
    const after = [contentId, ...before.filter((id) => id !== contentId)].slice(0, RECENT_VOD_LIMIT);
    await this.storage.writeLocal(CACHE_KEYS.recentVod, JSON.stringify(after));
    return after;
  }

  /**
   * L'ultimo canale guardato su questo apparecchio, o `null` se non se ne ricorda uno.
   *
   * Torna un identificativo e non una posizione: le posizioni cambiano a ogni import del catalogo, e
   * ripartire dalla numero 7 di un elenco riordinato significa aprire un canale a caso.
   */
  async lastChannel(): Promise<string | null> {
    return this.storage.readLocal(CACHE_KEYS.lastChannel);
  }

  async rememberChannel(channelId: string): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.lastChannel, channelId);
  }

  /**
   * I preferiti di questo apparecchio.
   *
   * Un elenco illeggibile torna vuoto invece di far esplodere l'avvio: i preferiti sono una comodità,
   * e nessuno deve restare fuori dalla televisione per un archivio corrotto.
   */
  async favorites(): Promise<ReadonlyArray<string>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.favorites);
    if (raw === null || raw === '') return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  async saveFavorites(ids: ReadonlyArray<string>): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.favorites, JSON.stringify(ids));
  }

  /**
   * I promemoria di questo apparecchio, come chiavi `canale@istante`.
   *
   * Stesso trattamento dei preferiti davanti a un archivio illeggibile: si torna vuoti invece di far
   * esplodere l'avvio. Un promemoria perso è una seccatura; restare fuori dalla televisione no.
   */
  async reminders(): Promise<ReadonlyArray<string>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.reminders);
    if (raw === null || raw === '') return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  async saveReminders(keys: ReadonlyArray<string>): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.reminders, JSON.stringify(keys));
  }

  /**
   * Le ultime ricerche. Un archivio illeggibile torna vuoto: è una comodità, non un dato da salvare.
   */
  async recentSearches(): Promise<ReadonlyArray<string>> {
    const raw = await this.storage.readLocal(CACHE_KEYS.recentSearches);
    if (raw === null || raw === '') return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  async saveRecentSearches(queries: ReadonlyArray<string>): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.recentSearches, JSON.stringify(queries));
  }

  /**
   * Le impostazioni di questo apparecchio.
   *
   * Si passano da `parseSettings` **sempre**, anche quando le ha scritte questa stessa versione: è
   * `localStorage`, ci mette le mani chiunque abbia una console aperta, e una versione futura
   * scriverà campi che questa non conosce. Un archivio illeggibile torna ai valori predefiniti — che
   * qui è la cosa giusta e non un ripiego, perché sono valori veri e funzionanti, non un segnaposto.
   */
  async settings(): Promise<PlayerSettings> {
    const raw = await this.storage.readLocal(CACHE_KEYS.settings);
    if (raw === null || raw === '') return DEFAULT_SETTINGS;
    try {
      return parseSettings(JSON.parse(raw));
    } catch {
      return DEFAULT_SETTINGS;
    }
  }

  async saveSettings(settings: PlayerSettings): Promise<void> {
    await this.storage.writeLocal(CACHE_KEYS.settings, JSON.stringify(settings));
  }

  /**
   * Il PIN del controllo genitori, o `null` se non ne è mai stato impostato uno.
   *
   * Passa da `readSecure`/`writeSecure` e non da `readLocal`: su una piattaforma che ha un archivio
   * protetto ci finisce dentro, e su una che non ce l'ha `secureBacking` lo dichiara invece di farlo
   * credere. È la stessa porta delle credenziali, per lo stesso motivo — quattro cifre che valgono
   * l'accesso a qualcosa.
   *
   * Si scarta quel che non sono quattro cifre: un archivio scritto a mano o da una versione
   * precedente non deve produrre un PIN che nessuno può digitare — cioè un blocco senza chiave.
   */
  async adultPin(): Promise<string | null> {
    const raw = await this.storage.readSecure(CACHE_KEYS.adultPin);
    if (raw === null) return null;
    return /^[0-9]{4}$/.test(raw) ? raw : null;
  }

  /** Archivia il PIN, oppure lo toglie quando è `null`. */
  async saveAdultPin(pin: string | null): Promise<void> {
    if (pin === null) await this.storage.removeSecure(CACHE_KEYS.adultPin);
    else await this.storage.writeSecure(CACHE_KEYS.adultPin, pin);
  }

  /**
   * Butta via tutto l'archivio del catalogo.
   *
   * Serve all'uscita dall'account: il catalogo di una linea non deve restare leggibile a quella
   * dopo. Le credenziali le cancella chi le ha scritte; qui si toglie quel che si è scritto qui.
   */
  async clear(): Promise<void> {
    await this.storage.removeLocal(CACHE_KEYS.channels);
    await this.storage.removeLocal(CACHE_KEYS.categories);
    await this.storage.removeLocal(CACHE_KEYS.lastChannel);
    await this.storage.removeLocal(CACHE_KEYS.favorites);
    await this.storage.removeLocal(CACHE_KEYS.movies);
    await this.storage.removeLocal(CACHE_KEYS.series);
    await this.storage.removeLocal(CACHE_KEYS.movieCategories);
    await this.storage.removeLocal(CACHE_KEYS.seriesCategories);
    /*
      **L'avanzamento di visione se ne va con l'account**, e vale la pena dirlo.

      È l'unica cosa qui dentro che chi guarda considererebbe *sua*: dove era arrivato in un film. Ma
      resta legato alla linea che lo ha prodotto, e lasciarlo vorrebbe dire che chi entra dopo trova
      «Continua a guardare» piena di titoli che non ha mai aperto — con i titoli di serie che
      raccontano cos'è stato guardato su quell'apparecchio prima di lui.
    */
    await this.storage.removeLocal(CACHE_KEYS.progress);
    await this.storage.removeLocal(CACHE_KEYS.recentVod);
    await this.storage.removeLocal(CACHE_KEYS.myList);
    // I promemoria se ne vanno con l'account: dicono cosa aveva intenzione di guardare chi c'era
    // prima, e puntano a canali che la linea dopo può non avere.
    await this.storage.removeLocal(CACHE_KEYS.reminders);
    // L'indice delle stagioni dice **quali serie sono state aperte** su questo apparecchio: se ne va
    // con l'avanzamento, per la stessa ragione scritta qui sopra.
    await this.storage.removeLocal(CACHE_KEYS.seriesSeasons);
    // Cosa ha cercato chi c'era prima non deve restare leggibile a chi entra dopo.
    await this.storage.removeLocal(CACHE_KEYS.recentSearches);
    await this.storage.removeLocal(USAGE_KEY);
    /*
      Le chiavi dell'EPG e delle schede sono tante e con un nome composto: si spazzano per prefisso.

      Qui c'era `localStorage.key(i)` direttamente, con un commento che dichiarava il debito —
      «l'adattatore di Tizen dovrà fare la sua parte». Il debito è scaduto quando la metà locale è
      passata a IndexedDB: quel ciclo leggeva un archivio in cui non c'è più niente, e avrebbe
      cancellato zero chiavi senza dire una parola. Adesso l'elenco lo chiede al port, che è l'unico a
      sapere dove tiene le proprie cose.
    */
    for (const prefix of [
      CACHE_KEYS.epgPrefix,
      CACHE_KEYS.seriesDetailPrefix,
      CACHE_KEYS.movieDetailPrefix,
    ]) {
      for (const key of await this.storage.listLocal(prefix)) {
        await this.storage.removeLocal(key);
      }
    }
  }
}

function toMap(list: ReadonlyArray<ChannelCategory>): ReadonlyMap<string, string> {
  return new Map(list.map((category) => [category.id, category.name]));
}
